import { TFilterSelected } from '@monorepo/types';
import * as _ from 'lodash';
import * as moment from 'moment';
import { TFilterGroupInfo, TFilterGroupModel } from './ui-filter.types';

const fillSelectedValues = <T>(filterGroup: TFilterGroupModel<T>): TFilterGroupModel<T> => {
  return {
    ...filterGroup,
    groups: filterGroup.groups.map((g) => {
      switch (g.type) {
        case 'ORG_HIERARCHY_FILTER': {
          return g;
        }
        case 'TARGET_DATE_FILTER': {
          return g;
        }
        case 'SELECT_FILTER': {
          const updatedItems = g.items.filter((v) => v.isSelected);
          return {
            ...g,
            selectedValues: updatedItems.map((v) => v.value),
          };
        }
        case 'MULTI_SELECT_FILTER': {
          const updatedItems = g.items.filter((v) => v.isSelected);
          return {
            ...g,
            selectedValues: updatedItems.map((v) => {
              return {
                value: v.value,
                displayName: v.displayName,
              };
            }),
          };
        }
        case 'SINGLE_SELECT_FILTER': {
          return g;
        }
        case 'DATE_FILTER': {
          const isDateFilterSelected = g.items.filter((v) => v.isSelected);
          return {
            ...g,
            selectedValues: isDateFilterSelected.map((v) => v.value),
          };
        }
        case 'DURATION_FILTER': {
          const isDateFilterSelected = g.items.filter((v) => v.isSelected);
          return {
            ...g,
            selectedValues: isDateFilterSelected.map((v) => v.value),
          };
        }
        case 'SEARCH_FILTER':
          return {
            ...g,
          };
      }
    }),
  };
};

const evaluateHasSelected = <T>(filterGroup: TFilterGroupModel<T>): TFilterGroupModel<T> => {
  return {
    ...filterGroup,
    hasSelectedFilters: !!filterGroup.groups.filter((v) => checkIfSelected(v)).length,
  };
};

const checkIfSelected = (item: TFilterGroupInfo) => {
  switch (item.type) {
    case 'ORG_HIERARCHY_FILTER':
      return item.filteredOrgTree.values.length;
    case 'SINGLE_SELECT_FILTER':
      return !!item.selectedValue;
    case 'SELECT_FILTER':
      return item.items.find((it) => it.isSelected);
    case 'MULTI_SELECT_FILTER':
      return item.items.find((it) => it.isSelected);
    case 'SEARCH_FILTER':
      return !!item.text;
    case 'DATE_FILTER':
      return item.items.find((v) => v.isSelected);
    case 'DURATION_FILTER':
      return item.items.find((v) => v.isSelected);
    case 'TARGET_DATE_FILTER':
      return item.selectedValues.length;
  }
};

const logFilterModel = <T>(filterGroup: TFilterGroupModel<T>): TFilterGroupModel<T> => {
  console.log('[LOG][FilterModel]', filterGroup);
  return filterGroup;
};

const selectedFilters = <T>(filterGroup: TFilterGroupModel<T>): TFilterGroupModel<T> => {
  const filterValue = filterGroup.groups.map((g) => {
    const versionObj =
      g.type && g.version
        ? {
            version: g.version,
            type: g.type,
          }
        : {};
    switch (g.type) {
      case 'ORG_HIERARCHY_FILTER':
        return {
          key: g.filteredOrgTree.key,
          displayName: g.filteredOrgTree.displayName,
          values: g.filteredOrgTree.values,
          ...versionObj,
        };
      case 'TARGET_DATE_FILTER':
        return {
          key: g.key,
          displayName: g.displayName,
          values: g.selectedValues,
          ...versionObj,
        };
      case 'SELECT_FILTER':
        return {
          key: g.key,
          values: g.items.filter((v) => v.isSelected).map((v) => v.value),
          ...(g.displayName && { displayName: g.displayName }),
          displayName: g.displayName,
        };
      case 'MULTI_SELECT_FILTER':
        return {
          key: g.key,
          displayName: g.displayName,
          values: g.items
            .filter((v) => v.isSelected)
            .map((v) => {
              return {
                value: v.value,
                displayName: v.displayName,
              };
            }),
          ...versionObj,
        };
      case 'SINGLE_SELECT_FILTER':
        return {
          key: g.key,
          values: g.selectedValue ? [g.selectedValue] : [],
        };
      case 'SEARCH_FILTER':
        return {
          key: g.key,
          values: [g.text],
        };
      case 'DATE_FILTER': {
        const finalValue = g.items.find((v) => v.isSelected);
        let values: string[] = [];
        if (finalValue) {
          switch (finalValue.value) {
            case 'PAST_ONE_DAY':
              values = [
                moment().subtract(1, 'days').format('YYYY-MM-DD').valueOf(),
                moment().subtract(1, 'days').format('YYYY-MM-DD').valueOf(),
              ];
              break;
            case 'PAST_ONE_WEEK':
              values = [
                moment().subtract(7, 'days').format('YYYY-MM-DD').valueOf(),
                moment().format('YYYY-MM-DD').valueOf(),
              ];
              break;
            case 'PAST_ONE_MONTH':
              values = [
                moment().subtract(30, 'days').format('YYYY-MM-DD').valueOf(),
                moment().format('YYYY-MM-DD').valueOf(),
              ];
              break;
          }
        }
        return {
          key: g.key,
          values,
          ...versionObj,
        };
      }
      case 'DURATION_FILTER': {
        return {
          key: g.key,
          values: g.items.filter((v) => v.isSelected).map((v) => v.value),
          ...versionObj,
        };
      }
    }
  });
  return {
    ...filterGroup,
    selectedFilters: (filterValue as TFilterSelected[]).filter((v) =>
      Array.isArray(v.values) ? v.values.length : v.values,
    ),
  };
};

const evaluateIsValid = <T>(filterGroup: TFilterGroupModel<T>): TFilterGroupModel<T> => {
  return {
    ...filterGroup,
    isValid: filterGroup.groups.every((group) => {
      switch (group.type) {
        case 'DURATION_FILTER':
        case 'SELECT_FILTER':
        case 'MULTI_SELECT_FILTER':
        case 'TARGET_DATE_FILTER': {
          return group.isRequired ? group.selectedValues.length > 0 : true;
        }
        case 'ORG_HIERARCHY_FILTER': {
          return group.isRequired ? group.filteredOrgTree.values.length > 0 : true;
        }

        case 'SINGLE_SELECT_FILTER': {
          return group.isRequired ? group.selectedValue !== undefined : true;
        }
        default:
          return false;
      }
    }),
  };
};
export const UiFilterTasks = {
  selectedFilters,
  evaluateHasSelected,
  fillSelectedValues,
  evaluateIsValid,
  logFilterModel,
};
